import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import "../styles/pages/game-day-challenges.scss";

const SPORTS = ["All sports", "NHL", "MLB", "NBA", "NFL"];
const ACHIEVEMENTS = [
  { key: "first_correct_pick", title: "On the Board", description: "Earn your first correct game-day pick.", icon: "🎯", points: 2 },
  { key: "underdog_call", title: "Underdog Call", description: "Correctly pick a winner chosen by fewer players.", icon: "🐕", points: 5 },
  { key: "pick_streak_3", title: "Three in a Row", description: "Get three game winners right in a row.", icon: "🔥", points: 3 },
  { key: "pick_streak_5", title: "On a Heater", description: "Get five game winners right in a row.", icon: "🌟", points: 5 },
  { key: "pick_streak_10", title: "Unstoppable", description: "Get ten game winners right in a row.", icon: "🏆", points: 10 },
];

const getLocalDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const GameDayChallenges = () => {
  const [date] = useState(getLocalDate);
  const [sport, setSport] = useState("All sports");
  const [challenges, setChallenges] = useState([]);
  const [picks, setPicks] = useState({});
  const [totalPoints, setTotalPoints] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadChallenges = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: invokeError } = await supabase.functions.invoke("game-day-polls", {
        body: { action: "load", challenge_date: date },
      });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);
      setChallenges(data?.challenges || []);
      setPicks(Object.fromEntries((data?.picks || []).map((pick) => [pick.challenge_id, pick])));
      setTotalPoints(data?.totalPoints || 0);
      setCurrentStreak(data?.currentStreak || 0);
      setBestStreak(data?.bestStreak || 0);
      setAchievements(data?.achievements || []);
    } catch (loadError) {
      console.error("Unable to load game-day polls:", loadError);
      setError(loadError.message || "Could not load today's picks.");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  const submitPick = async (challenge, teamId) => {
    setSubmittingId(challenge.id);
    setError("");
    setNotice("");
    try {
      const { data, error: pickError } = await supabase.rpc("submit_game_day_pick", {
        p_challenge_id: challenge.id,
        p_selected_team_id: teamId,
      });
      if (pickError) throw pickError;
      setPicks((current) => ({
        ...current,
        [challenge.id]: { challenge_id: challenge.id, selected_team_id: teamId, points_awarded: 0, id: data },
      }));
      setNotice("Pick locked in! Check back after the game to see if you scored a point.");
    } catch (pickError) {
      console.error("Unable to submit game-day pick:", pickError);
      setError(pickError.message || "Could not save your pick.");
    } finally {
      setSubmittingId(null);
    }
  };

  const filteredChallenges = useMemo(
    () => sport === "All sports" ? challenges : challenges.filter((challenge) => challenge.sport === sport),
    [challenges, sport]
  );
  const earnedKeys = new Set(achievements.map((achievement) => achievement.achievement_key));
  const perfectWeeks = achievements
    .filter((achievement) => achievement.achievement_key.startsWith("perfect_week_"))
    .map((achievement) => ({
      key: achievement.achievement_key,
      title: achievement.details?.title || "Perfect Week",
      description: achievement.details?.description || "Perfect game-day picks for a full week.",
      icon: "💯",
      points: achievement.bonus_points,
      earnedAt: achievement.earned_at,
    }));
  const achievementCards = [
    ...ACHIEVEMENTS.map((achievement) => ({
      ...achievement,
      earned: earnedKeys.has(achievement.key),
      earnedAt: achievements.find((item) => item.achievement_key === achievement.key)?.earned_at,
    })),
    ...perfectWeeks.map((achievement) => ({ ...achievement, earned: true })),
    { key: "perfect_week_next", title: "Perfect Week", description: "Get every pick right in a completed week with at least five picks.", icon: "💯", points: 10, earned: false },
  ];

  const renderTeam = (challenge, side) => {
    const isHome = side === "home";
    const teamId = isHome ? challenge.home_team_id : challenge.away_team_id;
    const teamName = isHome ? challenge.home_team_name : challenge.away_team_name;
    const teamAbbrev = isHome ? challenge.home_team_abbrev : challenge.away_team_abbrev;
    const logo = isHome ? challenge.home_logo : challenge.away_logo;
    const score = isHome ? challenge.home_score : challenge.away_score;
    const pick = picks[challenge.id];
    const selected = pick?.selected_team_id === teamId;
    const resolved = challenge.game_state === "final" || Boolean(challenge.resolved_at);
    const isWinner = resolved && challenge.winner_team_id === teamId;
    const isTie = resolved && !challenge.winner_team_id;
    const started = new Date(challenge.starts_at).getTime() <= Date.now();
    const canPick = !pick && !resolved && !started && challenge.game_state === "scheduled";

    return (
      <button
        key={side}
        type="button"
        className={`game-day-pick ${selected ? "is-selected" : ""} ${isWinner ? "is-winner" : ""} ${resolved && selected && !isWinner && !isTie ? "is-wrong" : ""}`}
        onClick={() => submitPick(challenge, teamId)}
        disabled={!canPick || submittingId === challenge.id}
        aria-pressed={selected}
        aria-label={`Pick ${teamName}`}
      >
        {logo ? <img src={logo} alt="" /> : <span className="game-day-pick__fallback">{teamAbbrev}</span>}
        <strong>{teamName}</strong>
        {resolved ? <span className="game-day-pick__score">{score ?? 0}</span> : <span className="game-day-pick__action">{selected ? "Your pick" : canPick ? "Pick winner" : "Picks closed"}</span>}
      </button>
    );
  };

  return (
    <main className="fb-page game-day-page">
      <section className="game-day-panel fb-card">
        <header className="game-day-header">
          <p className="fb-page__eyebrow">Make your call</p>
          <h1>Game-Day Challenges</h1>
          <p>Pick the winner before puck drop, first pitch, or kickoff. Every correct pick earns 1 point.</p>
        </header>

        <section className="game-day-points" aria-label="Your challenge points">
          <span aria-hidden="true">🏆</span>
          <div><strong>{totalPoints}</strong><small>Total challenge points</small></div>
          <div className="game-day-streak-summary">
            <strong>{currentStreak} <span>🔥</span></strong>
            <small>Current streak · best {bestStreak}</small>
          </div>
          <span className="game-day-points__hint">One pick per game · no tokens required</span>
        </section>

        <section className="game-day-achievements" aria-labelledby="game-day-achievements-title">
          <div className="game-day-achievements__header">
            <div>
              <p className="fb-page__eyebrow">Milestones</p>
              <h2 id="game-day-achievements-title">Streaks &amp; Achievements</h2>
            </div>
            <span>{achievements.length} earned</span>
          </div>
          <div className="game-day-achievements__grid">
            {achievementCards.map((achievement) => (
              <article className={`game-day-achievement ${achievement.earned ? "is-earned" : "is-locked"}`} key={achievement.key}>
                <span className="game-day-achievement__icon" aria-hidden="true">{achievement.icon}</span>
                <div>
                  <strong>{achievement.title}</strong>
                  <p>{achievement.description}</p>
                  {achievement.earned ? (
                    <small>Earned {new Date(achievement.earnedAt).toLocaleDateString()} · +{achievement.points} bonus {achievement.points === 1 ? "point" : "points"}</small>
                  ) : (
                    <small>Locked · +{achievement.points} bonus {achievement.points === 1 ? "point" : "points"}</small>
                  )}
                </div>
              </article>
            ))}
          </div>
          <p className="game-day-achievements__note">An upset is a correct pick for the less-popular team. Perfect Week is awarded after the week closes, with at least five resolved picks and no misses.</p>
        </section>

        <div className="game-day-toolbar">
          <label>
            <span>Filter by sport</span>
            <select className="form-select" value={sport} onChange={(event) => setSport(event.target.value)}>
              {SPORTS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <button className="fb-btn fb-btn--ghost" type="button" onClick={loadChallenges} disabled={loading || Boolean(submittingId)}>
            {loading ? "Refreshing…" : "Refresh scores"}
          </button>
        </div>

        {notice && <div className="alert alert-success" role="status">{notice}</div>}
        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        {loading ? (
          <div className="game-day-empty" role="status">Loading today's games…</div>
        ) : filteredChallenges.length ? (
          <div className="game-day-list">
            {filteredChallenges.map((challenge) => {
              const pick = picks[challenge.id];
              const completed = challenge.game_state === "final" || Boolean(challenge.resolved_at);
              const tied = completed && !challenge.winner_team_id;
              const started = new Date(challenge.starts_at).getTime() <= Date.now();
              const gameStatus = completed
                ? "Final"
                : challenge.game_state === "live" || started
                  ? "In progress · picks closed"
                  : `Starts ${new Date(challenge.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
              const pointMessage = completed && pick
                ? tied ? "Game tied — no point awarded." : pick.points_awarded > 0 ? "+1 point — nice call!" : "No point this time — get the next one!"
                : pick ? "Pick saved · result pending" : "Choose the team you think will win";

              return (
                <article className="game-day-card" key={challenge.id}>
                  <div className="game-day-card__heading">
                    <span className={`game-day-sport game-day-sport--${challenge.sport.toLowerCase()}`}>{challenge.sport}</span>
                    <span>{gameStatus}</span>
                  </div>
                  <h2>{challenge.question}</h2>
                  <div className="game-day-teams">
                    {renderTeam(challenge, "away")}
                    <span className="game-day-vs">VS</span>
                    {renderTeam(challenge, "home")}
                  </div>
                  <div className={`game-day-card__feedback ${completed && pick?.points_awarded ? "is-correct" : ""}`}>
                    {pick ? pointMessage : "Your pick is final once submitted."}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="game-day-empty">
            <span aria-hidden="true">📅</span>
            <h2>No games available{sport !== "All sports" ? ` for ${sport}` : ""} today</h2>
            <p>Check back on the next game day to make your picks.</p>
          </div>
        )}
      </section>
    </main>
  );
};

export default GameDayChallenges;
